"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const client_1 = require("@apollo/client");
const dev_1 = require("@apollo/client/dev");
const batch_http_1 = require("@apollo/client/link/batch-http");
const persisted_queries_1 = require("@apollo/client/link/persisted-queries");
const possibleTypes_1 = __importDefault(require("@aurora/shared-types/apollo/possibleTypes"));
const app_1 = require("@aurora/shared-types/app");
const SwitchBranchHelper_1 = require("@aurora/shared-utils/helpers/developer/SwitchBranchHelper");
const UrlHelper_1 = __importDefault(require("@aurora/shared-utils/helpers/urls/UrlHelper/UrlHelper"));
const log_1 = require("@aurora/shared-utils/log");
const apollo_upload_client_1 = require("apollo-upload-client");
const exenv_1 = require("exenv");
const apollo_1 = require("graphql-codegen-persisted-query-ids/lib/apollo");
const config_1 = __importDefault(require("next/config"));
const apolloCacheHelper_1 = require("./apolloCacheHelper");
const apolloFetch_1 = require("./apolloFetch");
const apolloTracingHelper_1 = require("./apolloTracingHelper");
const appHeaderLink_1 = __importDefault(require("./appHeaderLink"));
const authLink_1 = __importDefault(require("./authLink"));
const branchHeaderLink_1 = __importDefault(require("./branchHeaderLink"));
const errorLink_1 = __importDefault(require("./errorLink"));
const originLink_1 = __importDefault(require("./originLink"));
const parentFrameLink_1 = __importDefault(require("./parentFrameLink"));
const referrerLink_1 = __importDefault(require("./referrerLink"));
const timeoutLink_1 = __importDefault(require("./timeoutLink"));
const log = (0, log_1.getLog)(module);
const apqEnabled = process.env.NEXT_PUBLIC_APOLLO_APQ_ENABLED === 'true';
const parentFramesEnabled = process.env.NEXT_PUBLIC_ENABLE_PARENT_FRAMES === 'true';
// add any operations here which trigger the flood filter when called in a list of items
const batchOperations = new Set([
    'AttachmentList',
    'BoardDetails',
    'CommunityProperties',
    'CommunityPolicies',
    'ComponentServer',
    'ContentWorkflowProperties',
    'MembershipPolicies',
    'MessageCounts',
    'MessageCustomFieldsForView',
    'MessagePolicies',
    'ModerationData',
    'NodePolicies',
    'renderCustomComponent',
    'TagPolicies',
    'TagList'
]);
// Adds messages only in a dev environment
if (process.env.NODE_ENV === 'development') {
    (0, dev_1.loadDevMessages)();
    (0, dev_1.loadErrorMessages)();
}
/**
 * Creates and configures the ApolloClient
 * @param config app-specific apollo config
 * @param tenant the current tenant
 * @param initialToken an initial auth token if found
 * @param tokenResultCallback a callback to get a new auth token
 * @param logoutHandler handles user logout if 401 response
 * @param getRouteInfo
 * @param referrer
 * @param router
 * @param initialState
 */
const createApolloClient = (config, tenant, initialToken, tokenResultCallback, logoutHandler, getRouteInfo, referrer, router, initialState) => {
    var _a, _b;
    const now = performance.now();
    const { typePolicies = {}, resolvers = [], queryHashes = {}, appId } = config;
    // Check out https://github.com/zeit/next.js/pull/4611 if you want to use the AWSAppSyncClient
    const cache = new client_1.InMemoryCache({
        possibleTypes: possibleTypes_1.default,
        typePolicies
    }).restore(initialState !== null && initialState !== void 0 ? initialState : {});
    const defaultOptions = {
        query: {
            errorPolicy: 'all'
        },
        watchQuery: {
            errorPolicy: 'all'
        },
        mutate: {
            errorPolicy: 'all'
        }
    };
    // We need to manage the component tracing information because the ApolloLink
    // chain is called for each use of useQuery, however the BatchHttpLink will attempt to
    // aggregate queries made within a specified time period. For this reason we need to
    // modify the headers in a callback method that is triggered before the actual fetch
    // call is made. At which point we clear these arrays.
    let apiOriginRequest = {
        path: '',
        pageName: '',
        quiltId: '',
        fullUrl: '',
        operations: []
    };
    function onBeforeFetch(options) {
        (0, apolloTracingHelper_1.addTracingHeader)(options, apiOriginRequest);
        apiOriginRequest.operations = [];
    }
    function getCredentialsPolicy() {
        const credentialsPolicy = process.env.NEXT_PUBLIC_CREDENTIALS_POLICY;
        if (!credentialsPolicy) {
            return (tenant === null || tenant === void 0 ? void 0 : tenant.sameSite) ? 'include' : 'same-origin';
        }
        return credentialsPolicy;
    }
    function defaultFetch(input, options) {
        return (0, apolloFetch_1.apolloFetch)(input, options, onBeforeFetch);
    }
    function uploadFetch(input, options) {
        if (options.useUploadProgress) {
            return (0, apolloFetch_1.apolloXHR)(input, onBeforeFetch, options, tenant === null || tenant === void 0 ? void 0 : tenant.sameSite);
        }
        return defaultFetch(input, options);
    }
    const appIdHeaderLink = (0, appHeaderLink_1.default)(appId);
    const referrerLink = (0, referrerLink_1.default)(router, referrer);
    const callback = tokenResultCallback !== null && tokenResultCallback !== void 0 ? tokenResultCallback : (() => Promise.resolve(initialToken));
    const { authLink } = (0, authLink_1.default)(initialToken, callback, logoutHandler);
    const httpOptions = {
        fetchOptions: {
            dispatcher: globalThis.apolloFetchAgent
        },
        credentials: getCredentialsPolicy(),
        fetch: defaultFetch,
        uri: operation => UrlHelper_1.default.getGraphQlApiUrl(tenant).addQuery(UrlHelper_1.default.getGraphQlApiParams(operation)).build()
    };
    const batchHttpLink = new batch_http_1.BatchHttpLink(Object.assign(Object.assign({}, httpOptions), { batchDebounce: true, batchMax: 5 }));
    const httpLink = new client_1.HttpLink(httpOptions);
    const defaultHttpLink = client_1.ApolloLink.split(op => batchOperations.has(op.operationName), batchHttpLink, httpLink);
    const uploadHttpLink = (0, apollo_upload_client_1.createUploadLink)(Object.assign(Object.assign({}, httpOptions), { fetch: uploadFetch }));
    const splitLink = client_1.ApolloLink.split(op => {
        const keys = Object.keys(op.variables);
        return keys.includes('file') || keys.includes('assetPart');
    }, uploadHttpLink, defaultHttpLink);
    const fetchPolicyLink = new client_1.ApolloLink((operation, forward) => {
        return forward(operation).map(data => {
            (0, apolloCacheHelper_1.updateCacheFetchPolicy)(operation.query);
            return data;
        });
    });
    const apolloLinks = [
        timeoutLink_1.default,
        appIdHeaderLink,
        referrerLink,
        (0, errorLink_1.default)(tenant),
        originLink_1.default,
        authLink,
        fetchPolicyLink
    ];
    if (appId === app_1.AppType.END_USER && (0, SwitchBranchHelper_1.isBranchSwitchingEnabled)(tenant)) {
        apolloLinks.push((0, branchHeaderLink_1.default)());
    }
    if (parentFramesEnabled) {
        apolloLinks.push(parentFrameLink_1.default);
    }
    if (apqEnabled) {
        const hashLookup = (0, apollo_1.usePregeneratedHashes)(queryHashes);
        const apqLink = (0, persisted_queries_1.createPersistedQueryLink)({
            generateHash: hashLookup
        });
        const apqSplitLink = client_1.ApolloLink.split(op => {
            try {
                const useHash = hashLookup(op.query) !== null;
                log.trace('Use APQ for query: %s: %s', op.operationName, useHash);
                return useHash;
            }
            catch (_a) {
                log.debug('Error looking up APQ hash for query %s', op.operationName);
                return false;
            }
        }, apqLink);
        apolloLinks.push(apqSplitLink);
    }
    // We need to create this ApolloLink in the same scope that also manages
    // the array of `componentTracings` given the interplay between the Apollo
    // link chain and the fetch.
    if (getRouteInfo && (0, apolloTracingHelper_1.isRequestOriginEnabled)()) {
        const tracingApolloLink = new client_1.ApolloLink((operation, forward) => {
            const handleApiOriginRequest = function () {
                return __awaiter(this, void 0, void 0, function* () {
                    var _a;
                    const { pageName, path } = yield getRouteInfo();
                    apiOriginRequest = (0, apolloTracingHelper_1.updateLocationInformationOnApiOriginRequest)(apiOriginRequest, {
                        path: path ? path.split('?')[0] : '',
                        pageName: pageName,
                        fullUrl: `${tenant.auroraHost}${path !== null && path !== void 0 ? path : ''}`
                    });
                    operation.setContext(Object.assign({}, apiOriginRequest));
                    // The `tracing` context property is set in the `useQueryWithTracing` hook.
                    const { apiOriginOperation, quilt } = operation.getContext();
                    if (apiOriginOperation) {
                        apiOriginRequest.quiltId = (_a = quilt === null || quilt === void 0 ? void 0 : quilt.id) !== null && _a !== void 0 ? _a : 'not-set (Aurora)';
                        apiOriginRequest.operations.push(apiOriginOperation);
                    }
                });
            };
            return (0, client_1.fromPromise)(handleApiOriginRequest()).flatMap(() => forward(operation));
        });
        apolloLinks.push(tracingApolloLink);
    }
    apolloLinks.push(splitLink);
    const apolloClient = new client_1.ApolloClient({
        // name: 'AuroraApolloClient',
        // version: '0.1',
        defaultOptions,
        ssrMode: !exenv_1.canUseDOM, // Disables forceFetch on the server (so queries are only run once)
        link: client_1.ApolloLink.from(apolloLinks),
        cache,
        resolvers,
        queryDeduplication: true,
        devtools: {
            enabled: exenv_1.canUseDOM && ((_b = (_a = (0, config_1.default)()) === null || _a === void 0 ? void 0 : _a.publicRuntimeConfig) === null || _b === void 0 ? void 0 : _b.apolloDevToolsEnabled)
        }
    });
    if (!tenant) {
        cache.reset();
    }
    if (getRouteInfo) {
        getRouteInfo()
            .then(({ pageName, path }) => {
            const route = pageName !== null && pageName !== void 0 ? pageName : path;
            return log.debug('New apollo client created in %dms' + (route ? ' for %s' : ''), performance.now() - now, route);
        })
            .catch(error => {
            log.debug('New apollo client created in %dms.', performance.now() - now, error);
        });
    }
    else {
        log.trace('New apollo client created in %dms', performance.now() - now);
    }
    return apolloClient;
};
exports.default = createApolloClient;
//# sourceMappingURL=apolloClient.js.map